import { ACTIONS } from "@wallyo/contracts/actions";
import { PERMISSIONS } from "@wallyo/contracts/permissions";
import { SETTINGS } from "@wallyo/contracts/settings";

import type { FeatureNames } from "./feature-names";

/**
 * The English names, taken straight from the catalogs (#185). English is the
 * source: a definition carries its own `name`, so there is nothing to keep in
 * step here. The other seven languages translate these, in features.<lang>.ts.
 */
const named = <T extends string>(entries: readonly { id: T; name: string }[]): Record<T, string> =>
  Object.fromEntries(entries.map((each) => [each.id, each.name])) as Record<T, string>;

const settings = SETTINGS.map((setting) => ({ id: setting.settingId, name: setting.name }));
const staffFeature = (id: string) => id.endsWith("_STAFF_FEATURE_ENABLEMENT");
const customerFeature = (id: string) => id.endsWith("_USER_FEATURE_ENABLEMENT");
const ownSetting = (id: string) => id.endsWith("_USER_SETTING") || id.endsWith("_STAFF_SETTING");

export const featuresEn: FeatureNames = {
  features: named(settings.filter((each) => staffFeature(each.id)) as never),
  permissions: named(PERMISSIONS.map((each) => ({ id: each.settingId, name: each.name })) as never),
  customerFeatures: named(settings.filter((each) => customerFeature(each.id)) as never),
  actions: named(ACTIONS.map((each) => ({ id: each.actionId, name: each.name })) as never),
  settings: named(settings.filter((each) => ownSetting(each.id)) as never),
  categories: {
    appearance: "Appearance",
    notifications: "Notifications",
    leads: "Leads",
    discovery: "Search",
    jobs: "Job requests",
    profile: "Profile",
    accounts: "Accounts",
    support: "Cases",
    onBehalf: "On behalf of a customer",
    onboarding: "Verification",
    payments: "Payments",
    safety: "Trust & safety",
    messaging: "Messages",
    configuration: "Configuration",
    staff: "Team & access",
    audit: "Audit",
  },
};
